import { Controller, useFieldArray, useForm } from "react-hook-form";
import { type SxProps, type Theme } from "@mui/material/styles";

import Icons from "assets";
import { Button } from "ui/button";
import { TextField } from "ui/text-field";
import { Switch } from "ui/switch";

import type { EmailType } from "hooks/api/use-email-types";

import type { IterableEmailSenderDetailRequest } from "generated/schemas";

type SenderFormValues = {
  senders: IterableEmailSenderDetailRequest[];
};

export type CollegeProfileEmailTypeSendersFormProps = {
  emailType: EmailType;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (senders: IterableEmailSenderDetailRequest[]) => void;
};

const DELETE_SENDER_BUTTON_SX: SxProps<Theme> = { alignSelf: "flex-start" };

const EMPTY_SENDER: IterableEmailSenderDetailRequest = {
  senderName: "",
  senderEmail: "",
  replyToEmail: "",
  defaultForEmailType: false,
};

/**
 * Bulk-edit form for an email type's sender details. `useUpdateEmailTypeSenderDetails`
 * replaces the whole `senders` array in one request, so all rows are edited
 * together rather than saved per-row.
 */
const CollegeProfileEmailTypeSendersForm = ({
  emailType,
  isSaving,
  onCancel,
  onSave,
}: CollegeProfileEmailTypeSendersFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<SenderFormValues>({
    defaultValues: { senders: emailType.senders?.length ? emailType.senders : [EMPTY_SENDER] },
  });

  const { fields, append, remove } = useFieldArray({ name: "senders", control });

  const handleMakeDefault = (index: number) => {
    fields.forEach((_field, fieldIndex) => {
      setValue(`senders.${fieldIndex}.defaultForEmailType`, fieldIndex === index);
    });
  };

  const onSubmit = (values: SenderFormValues) => {
    onSave(values.senders);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
      {fields.map((field, index) => (
        <section key={field.id} className="flex flex-col gap-2 rounded-md border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-navy-800">Sender {index + 1}</span>
            <Controller
              name={`senders.${index}.defaultForEmailType`}
              control={control}
              render={({ field: toggleField }) => (
                <Switch
                  toggleName="Default"
                  checked={!!toggleField.value}
                  onChange={(checked) => checked && handleMakeDefault(index)}
                />
              )}
            />
          </div>
          <TextField
            required
            label="Sender name"
            errMessage={errors.senders?.[index]?.senderName?.message}
            {...register(`senders.${index}.senderName`, { required: "Sender name is required." })}
          />
          <TextField
            required
            label="Sender email"
            errMessage={errors.senders?.[index]?.senderEmail?.message}
            {...register(`senders.${index}.senderEmail`, { required: "Sender email is required." })}
          />
          <TextField
            required
            label="Reply-to email"
            errMessage={errors.senders?.[index]?.replyToEmail?.message}
            {...register(`senders.${index}.replyToEmail`, {
              required: "Reply-to email is required.",
            })}
          />
          {fields.length > 1 && (
            <Button
              size="small"
              variant="text"
              startIcon={<Icons.Trash />}
              onClick={() => remove(index)}
              sx={DELETE_SENDER_BUTTON_SX}
            >
              Delete
            </Button>
          )}
        </section>
      ))}
      <div className="flex items-center gap-3">
        <Button size="small" variant="outlined" onClick={() => append(EMPTY_SENDER)}>
          Create sender details
        </Button>
      </div>
      <div className="flex gap-3">
        <Button variant="contained" type="submit" loading={isSaving}>
          Save changes
        </Button>
        <Button variant="text" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default CollegeProfileEmailTypeSendersForm;
